import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const GoalCard = ({ goal, onEdit, onComplete, onDelete }) => {
  const [showDetails, setShowDetails] = useState(false);

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'bg-success';
    if (progress >= 60) return 'bg-accent';
    if (progress >= 40) return 'bg-warning';
    return 'bg-error';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return { name: 'CheckCircle', color: 'text-success' };
      case 'in-progress':
        return { name: 'Clock', color: 'text-accent' };
      case 'paused':
        return { name: 'Pause', color: 'text-warning' };
      default:
        return { name: 'Circle', color: 'text-muted-foreground' };
    }
  };

  const statusIcon = getStatusIcon(goal?.status);
  const daysRemaining = Math.ceil((new Date(goal.targetDate) - new Date()) / (1000 * 60 * 60 * 24));

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-wellness hover:shadow-wellness-md transition-wellness">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className={`w-12 h-12 rounded-lg ${goal?.category === 'fitness' ? 'bg-success/10' : 
            goal?.category === 'nutrition' ? 'bg-accent/10' : 
            goal?.category === 'mindfulness' ? 'bg-primary/10' : 'bg-secondary/10'} 
            flex items-center justify-center`}>
            <Icon 
              name={goal?.category === 'fitness' ? 'Dumbbell' : 
                   goal?.category === 'nutrition' ? 'Apple' : 
                   goal?.category === 'mindfulness' ? 'Brain' : 'Users'} 
              size={24} 
              className={goal?.category === 'fitness' ? 'text-success' : 
                        goal?.category === 'nutrition' ? 'text-accent' : 
                        goal?.category === 'mindfulness' ? 'text-primary' : 'text-secondary'} 
            />
          </div>
          <div>
            <h3 className="text-lg font-heading font-semibold text-foreground">{goal?.title}</h3>
            <p className="text-sm text-muted-foreground capitalize">{goal?.category} Goal</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <Icon name={statusIcon?.name} size={20} className={statusIcon?.color} />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowDetails(!showDetails)}
          >
            <Icon name={showDetails ? "ChevronUp" : "ChevronDown"} size={16} />
          </Button>
        </div>
      </div>
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-foreground">Progress</span>
          <span className="text-sm font-semibold text-foreground">{goal?.progress}%</span>
        </div>
        <div className="w-full bg-muted rounded-full h-3">
          <div 
            className={`h-3 rounded-full transition-all duration-500 ${getProgressColor(goal?.progress)}`}
            style={{ width: `${goal?.progress}%` }}
          ></div>
        </div>
      </div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4">
          <div className="text-center">
            <p className="text-2xl font-heading font-bold text-foreground">{goal?.currentValue}</p>
            <p className="text-xs text-muted-foreground">Current</p>
          </div>
          <Icon name="ArrowRight" size={16} className="text-muted-foreground" />
          <div className="text-center">
            <p className="text-2xl font-heading font-bold text-primary">{goal?.targetValue}</p>
            <p className="text-xs text-muted-foreground">Target</p>
          </div>
        </div>
        
        <div className="text-right">
          <p className="text-sm font-medium text-foreground">
            {daysRemaining > 0 ? `${daysRemaining} days left` : 'Overdue'}
          </p>
          <p className="text-xs text-muted-foreground">
            Due {new Date(goal.targetDate)?.toLocaleDateString()}
          </p>
        </div>
      </div>
      {showDetails && (
        <div className="border-t border-border pt-4 mb-4">
          <p className="text-sm text-muted-foreground mb-3">{goal?.description}</p>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-center space-x-2 mb-1">
                <Icon name="Calendar" size={16} className="text-primary" />
                <span className="text-xs font-medium text-foreground">Started</span>
              </div>
              <p className="text-sm text-foreground">{new Date(goal.startDate)?.toLocaleDateString()}</p>
            </div>
            
            <div className="bg-muted/50 rounded-lg p-3">
              <div className="flex items-center space-x-2 mb-1">
                <Icon name="Zap" size={16} className="text-accent" />
                <span className="text-xs font-medium text-foreground">Streak</span>
              </div>
              <p className="text-sm text-foreground">{goal?.streak} days</p>
            </div>
          </div>

          {goal?.milestones && goal?.milestones?.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-foreground mb-2">Milestones</h4>
              <div className="space-y-2">
                {goal?.milestones?.map((milestone, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <Icon 
                      name={milestone?.completed ? "CheckCircle" : "Circle"} 
                      size={16} 
                      className={milestone?.completed ? "text-success" : "text-muted-foreground"} 
                    />
                    <span className={`text-sm ${milestone?.completed ? 'text-foreground line-through' : 'text-muted-foreground'}`}>
                      {milestone?.title}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      <div className="flex items-center space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onEdit(goal)}
          iconName="Edit"
          iconPosition="left"
        >
          Edit
        </Button>
        
        {goal?.status !== 'completed' && (
          <Button
            variant="default"
            size="sm"
            onClick={() => onComplete(goal)}
            iconName="CheckCircle"
            iconPosition="left"
          >
            Complete
          </Button>
        )}
        
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(goal?.id)}
          iconName="Trash2"
          iconPosition="left"
          className="text-error hover:text-error"
        >
          Delete
        </Button>
      </div>
    </div>
  );
};

export default GoalCard;